import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import Moment from 'react-moment';
import { ROLE, STATUS_APPLICATION_SUPPLY } from 'helpers/constants';
import { ApplicationSupplyService } from 'services';
import {
  useWebSocketApplicationSupplies,
  useGetApplicationSupplies,
  useEditApplication,
  useIsRole,
  useIsManager,
  useStore,
  queryForApi,
} from 'hooks';
import Menu from 'components/menu';
import Table from 'components/table';
import Status from 'components/status';
import Modal from 'components/modal';
import Application from 'components/modal/order/supply';
import ActionsTable from 'components/table/actions';
import { Input } from 'components/ui/form';
import { Filter, Actions } from 'components/applications';
import styles from '../applications.module.scss';

const SUPPLY_ARCHIVE_MODE_STORAGE_KEY = 'granit_filters_supply_main';

const scrollMainToTop = () => {
  const main = document.querySelector('main');
  if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
};

const openSupplyPrint = (id) => {
  const frame = document.createElement('iframe');
  frame.title = 'Печатная форма заявки';
  frame.src = `/applications/supply/print/${id}?embedded=1`;
  frame.style.position = 'fixed';
  frame.style.width = '1px';
  frame.style.height = '1px';
  frame.style.border = '0';
  frame.style.opacity = '0';
  frame.style.pointerEvents = 'none';

  const removeFrame = () => {
    if (frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }
  };

  const handleMessage = (event) => {
    if (event.source === frame.contentWindow && event.data?.type === 'SUPPLY_PRINT_FINISHED') {
      window.removeEventListener('message', handleMessage);
      removeFrame();
    }
  };

  window.addEventListener('message', handleMessage);
  frame.addEventListener('error', () => {
    window.removeEventListener('message', handleMessage);
    removeFrame();
  }, { once: true });
  document.body.appendChild(frame);
};

const ApplicationSupplyPage = ({
  history,
  location: { pathname, search },
  match: {
    params: { userId },
  },
}) => {
  const { setUser, setLoading, setReload } = useStore();
  const isAdminOrManager = useIsRole([ROLE.ADMIN, ROLE.MANAGER]);
  const isManager = useIsManager();
  const isArchivePage = pathname.includes('/applications/supply/archive');
  const [archiveMode, setArchiveMode] = useState(() => {
    if (isManager) {
      return 'active';
    }
    const fallback = isArchivePage ? 'archive' : 'active';
    try {
      const stored = localStorage.getItem(SUPPLY_ARCHIVE_MODE_STORAGE_KEY);
      return stored === 'archive' || stored === 'active' ? stored : fallback;
    } catch {
      return fallback;
    }
  });
  const archiveInitialQuery = useMemo(() => ({
    archivePeriod: 'all',
  }), []);
  const [
    data,
    filters,
    query,
    setData,
    setQueryAndResetPage,
    totalCount,
    page,
    limit,
    setPage,
    setLimit,
    resetFilters,
  ] = useGetApplicationSupplies(userId, true, {
    storageKeySuffix: archiveMode === 'archive' ? 'archive' : 'main',
    initialQuery: archiveMode === 'archive' ? archiveInitialQuery : {},
    isArchive: archiveMode === 'archive',
  });
  const [edit] = useEditApplication(data, search);
  const [selected, setSelected] = useState([]);
  const [selectAllInFilter, setSelectAllInFilter] = useState(false);
  const isProfile = isAdminOrManager && Number(userId) >= 0;
  const isFirstRender = useRef(true);

  useWebSocketApplicationSupplies(userId, data, query, setData);

  useEffect(() => {
    if (isManager && archiveMode !== 'active') {
      setArchiveMode('active');
    }
  }, [isManager, archiveMode]);

  useEffect(() => {
    try {
      localStorage.setItem(SUPPLY_ARCHIVE_MODE_STORAGE_KEY, archiveMode);
    } catch {
      // ignore localStorage errors
    }

    setData([]);
    setQueryAndResetPage({
      tonMin: '',
      tonMax: '',
    });
    setPage(0);
  }, [archiveMode]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollMainToTop();
  }, [query]);

  const onExport = async () => {
    setLoading(true);
    const body = selectAllInFilter
      ? { exportAllInFilter: true, userId, ...queryForApi(query) }
      : {
        selected: selected.filter(id => data.some(({ id: _id }) => _id === id)),
        userId,
      };
    const blob = await new ApplicationSupplyService({ setUser }).exportApplications(body);
    setLoading(false);

    if (blob) {
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = href;
      link.download = 'applications.xml';
      link.click();
    }
  };

  const onImport = async (e) => {
    const { target: { form, files } } = e;

    if (files.length) {
      const file = files[0];
      const formData = new FormData();
      formData.append('file', file, 'file.xml');
      if (userId) {
        formData.append('userId', userId);
      }

      setLoading(true);
      await new ApplicationSupplyService({ setUser }).importApplications(formData);
      setLoading(false);

      setReload();
    }

    form.reset();
  };

  const onCloseModal = (isReload = false) => {
    history.push({ pathname });

    if (isReload) {
      setReload();
    }
  };

  const onSelect = ({ target: { value, checked } }) => {
    const v = Number(value);

    if (selectAllInFilter) {
      setSelectAllInFilter(false);
      const allIds = data.map(({ id }) => id);
      const result = checked
        ? allIds
        : allIds.filter(id => id !== v);
      setSelected(result);
      return;
    }

    let result = [...selected];

    if (checked) {
      if (!result.includes(v)) {
        result.push(v);
      }
    } else {
      result = result.filter(id => id !== v);
    }

    setSelected(result);
  };

  const isSelectedAll = selectAllInFilter
    || (data.length > 0 && data.every(({ id }) => selected.includes(id)));

  const onSelectAll = () => {
    if (isSelectedAll) {
      setSelectAllInFilter(false);
      setSelected([]);
    } else {
      setSelectAllInFilter(true);
      setSelected([]);
    }
  };

  const cells = [...(isAdminOrManager ? [{
    keyName: 'selectAll',
    onRenderName: () => (
      <input
        type="checkbox"
        checked={isSelectedAll}
        onChange={onSelectAll}
      />
    ),
    onRender: ({ id }) => (
      <input
        type="checkbox"
        value={id}
        checked={selectAllInFilter || selected.includes(id)}
        onChange={onSelect}
      />
    ),
  }] : []), {
    name: 'Номер',
    renderField: 'id',
    nowrap: true,
  }, {
    name: '№ заявки на вагоны',
    nowrap: true,
    onRender: item => {
      const id = item?.applicationWagon?.id ?? item?.applicationWagonId;
      return id != null ? id : '—';
    },
  }, {
    name: 'Дата',
    renderField: 'date',
    nowrap: true,
    onRender: (item, value) => <Moment>{value}</Moment>,
  }, {
    name: 'Грузополучатель',
    onRender: item => item?.applicationWagon?.userConsignee?.consignee?.name || 'Прочие (9999)',
  }, {
    name: 'Продукт',
    onRender: item => item?.contractProduct?.product?.name || '',
  }, {
    name: 'Отсрочка платежа',
    nowrap: true,
    onRender: item => {
      const contract = item?.applicationWagon?.contract;
      const isProvided = Number(contract?.isPaymentDeferralProvided) === 1;
      const endDate = contract?.paymentDeferralEndDate;

      if (!isProvided) {
        return 'Нет';
      }

      return <Moment format="DD.MM.YYYY">{endDate}</Moment>;
    },
  }, {
    name: 'Кол-во тонн',
    renderField: 'ton',
    nowrap: true,
  }, {
    name: 'Станция назначения',
    wrapText: true,
    onRender: item => item?.applicationWagon?.station?.name,
  }, {
    name: 'Статус',
    renderField: 'status',
    nowrap: true,
    onRender: (item, value) => (
      <Status
        value={value}
        status={STATUS_APPLICATION_SUPPLY}
      />
    ),
  }, {
    keyName: 'actions',
    right: true,
    nowrap: true,
    onRender: ({ id }) => (
      <ActionsTable
        data={archiveMode === 'archive'
          ? (isAdminOrManager ? [{
            name: 'print',
            title: 'Печатная форма / Сохранить в PDF',
            onClick: () => openSupplyPrint(id),
          }] : [])
          : [
            {
              name: 'edit',
              title: 'Редактировать',
              onClick: () => {
                history.push({
                  pathname,
                  search: `?id=${id}`,
                });
              },
            },
            ...(isAdminOrManager ? [{
              name: 'print',
              title: 'Печатная форма / Сохранить в PDF',
              onClick: () => openSupplyPrint(id),
            }] : []),
          ]}
      />
    ),
  }];

  return (
    <>
      {isProfile && (
        <div className={styles.menu}>
          <Menu />
        </div>
      )}
      {isAdminOrManager ? (
        <Actions
          onExport={onExport}
          onImport={onImport}
        />
      ) : null}
      <div className={styles.search}>
        <Input
          type="text"
          value={query.search || ''}
          onChange={e => setQueryAndResetPage({ search: e.target.value })}
          placeholder="Поиск по заявке, направлению, грузополучателю, станции, договору"
        />
      </div>
      <Filter
        filters={filters}
        query={query}
        setQuery={setQueryAndResetPage}
        showFilter
        showArchiveModeToggle={!isManager}
        archiveMode={archiveMode}
        onArchiveModeChange={setArchiveMode}
        onResetFilters={resetFilters}
      />
      <Table
        data={data}
        cells={cells}
        count={totalCount}
        page={page}
        limit={limit}
        onPageChange={({ selected: selectedPage }) => setPage(selectedPage)}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(0);
        }}
      />
      {edit ? (
        <Modal closeBackground={false} closeEsc onClose={onCloseModal}>
          <Application edit={edit} onClose={onCloseModal} />
        </Modal>
      ) : null}
    </>
  );
};

export default ApplicationSupplyPage;
